<template>
  <div class="referral-page">
    <div class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <button class="back-btn" @click="goBack">
          <feather-icon icon="ChevronLeftIcon" size="20" />
        </button>
        <h1 class="page-title">ชวนเพื่อน</h1>
        <button class="info-btn" @click="showInfoModal = true">
          <feather-icon icon="InfoIcon" size="20" />
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-container">
        <b-spinner variant="warning" />
        <p class="loading-text">กำลังโหลด...</p>
      </div>

      <!-- Load Error -->
      <div v-else-if="loadError" class="state-card">
        <span class="state-icon">⚠️</span>
        <p class="state-text">โหลดข้อมูลไม่สำเร็จ</p>
        <button class="retry-btn" @click="initialize">ลองใหม่อีกครั้ง</button>
      </div>

      <!-- Feature Disabled -->
      <div v-else-if="!summary || !summary.enabled" class="state-card">
        <span class="state-icon">🔒</span>
        <p class="state-text">ระบบชวนเพื่อนยังไม่เปิดให้บริการ</p>
        <p class="state-subtext">โปรดติดตามเร็ว ๆ นี้</p>
      </div>

      <!-- Main Content -->
      <div v-else class="main-content">
        <!-- Invite Link Card -->
        <div class="glass-card link-card">
          <div class="card-label">ลิงก์ชวนเพื่อนของคุณ</div>
          <div class="code-row">
            <span class="invite-code">{{ summary.invite_code }}</span>
            <span class="code-emoji">👥</span>
          </div>
          <div class="invite-link">{{ summary.invite_link }}</div>
          <div class="btn-row">
            <button class="action-btn primary-btn" @click="copyLink">
              {{ hasCopied ? '✓ คัดลอกแล้ว' : '📋 คัดลอกลิงก์' }}
            </button>
            <button class="action-btn line-btn" @click="shareLine">
              แชร์ LINE
            </button>
          </div>
        </div>

        <!-- Commission Hero Card -->
        <div class="glass-card hero-card">
          <div class="card-label">คอมมิชชั่นที่รับได้</div>
          <div class="hero-amount">
            ฿{{ formatNumber(summary.claimable_total) }}
          </div>

          <template v-if="belowMinimum">
            <div class="progress-track">
              <div
                class="progress-fill"
                :style="{ width: `${claimProgress}%` }"
              />
            </div>
            <div class="hero-hint">
              อีก ฿{{
                formatNumber(summary.min_claim_amount - summary.claimable_total)
              }}
              รับได้เลย (ขั้นต่ำ
              {{ formatNumber(summary.min_claim_amount) }} บาท)
            </div>
          </template>
          <div v-else class="hero-hint">
            ขั้นต่ำในการรับ {{ formatNumber(summary.min_claim_amount) }} บาท
          </div>

          <button
            class="action-btn primary-btn block-btn"
            :disabled="belowMinimum || isClaiming"
            @click="openClaimSheet"
          >
            รับคอมมิชชั่น
          </button>
        </div>

        <!-- Split Stats -->
        <div class="split-row">
          <div class="glass-card stat-card">
            <div class="card-label">รอปิดรอบ</div>
            <div class="stat-amount">
              ฿{{ formatNumber(summary.pending_total) }}
            </div>
          </div>
          <div class="glass-card stat-card">
            <div class="card-label">รับไปแล้ว</div>
            <div class="stat-amount">
              ฿{{ formatNumber(summary.claimed_total) }}
            </div>
          </div>
        </div>

        <!-- Rates Strip -->
        <div class="rates-strip">
          <span class="rates-icon">💰</span>
          <span>
            รับ
            <strong
              >{{
                ratePercent(summary.rates.first_deposit_level1_rate)
              }}%</strong
            >
            จากฝากแรกของเพื่อน และ
            <strong
              >{{ ratePercent(summary.rates.winloss_level1_rate) }}%</strong
            >
            จากยอดเสีย{{ periodLabel(summary.periods.winloss) }}
            <template v-if="summary.level2_enabled">(2 ชั้น)</template>
          </span>
        </div>

        <!-- Tabs -->
        <div class="tab-row">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'friends' }"
            @click="switchTab('friends')"
          >
            เพื่อนของฉัน ({{ totalFriends }})
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'history' }"
            @click="switchTab('history')"
          >
            ประวัติคอมมิชชั่น
          </button>
        </div>

        <!-- Friends Tab -->
        <template v-if="activeTab === 'friends'">
          <div class="chip-row">
            <button
              v-for="chip in friendChips"
              :key="chip.key"
              class="chip"
              :class="{ active: friendFilter === chip.key }"
              @click="setFriendFilter(chip.key)"
            >
              {{ chip.label }}
            </button>
          </div>

          <div class="glass-card list-card">
            <div
              v-if="friends.length === 0 && !friendsLoading"
              class="empty-state"
            >
              <span class="empty-icon">👥</span>
              <p class="empty-text">ยังไม่มีเพื่อนสมัครผ่านลิงก์ของคุณ</p>
              <p class="empty-subtext">
                แชร์ลิงก์วันนี้ รับสูงสุด
                {{ ratePercent(summary.rates.first_deposit_level1_rate) }}%
                จากฝากแรก
              </p>
              <button class="action-btn primary-btn" @click="copyLink">
                📋 คัดลอกลิงก์เลย
              </button>
            </div>

            <template v-else>
              <div
                v-for="(friend, index) in friends"
                :key="index"
                class="list-row tappable"
                @click="openFriendDetail(friend)"
              >
                <div class="row-avatar">🧑</div>
                <div class="row-main">
                  <div class="row-name">{{ friend.phone_masked }}</div>
                  <div class="row-sub">
                    สมัคร {{ formatDate(friend.registered_at) }}
                  </div>
                </div>
                <span
                  class="tag"
                  :class="friend.level === 1 ? 'tag-l1' : 'tag-l2'"
                >
                  ชั้น {{ friend.level }}
                </span>
                <span v-if="friend.has_deposited" class="tag tag-deposit"
                  >ฝากแล้ว</span
                >
                <span class="row-amount" :class="{ zero: friend.earned <= 0 }">
                  {{
                    friend.earned > 0
                      ? `+฿${formatNumber(friend.earned)}`
                      : '฿0'
                  }}
                </span>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="14"
                  class="row-chevron"
                />
              </div>

              <button
                v-if="friendsHasMore"
                class="load-more-btn"
                :disabled="friendsLoading"
                @click="fetchFriends()"
              >
                {{ friendsLoading ? 'กำลังโหลด...' : 'โหลดเพิ่ม' }}
              </button>
            </template>
          </div>
        </template>

        <!-- History Tab -->
        <template v-else>
          <div class="chip-row">
            <button
              v-for="chip in historyChips"
              :key="chip.key"
              class="chip"
              :class="{ active: historyFilter === chip.key }"
              @click="setHistoryFilter(chip.key)"
            >
              {{ chip.label }}
            </button>
          </div>

          <div class="glass-card list-card">
            <div
              v-if="history.length === 0 && !historyLoading"
              class="empty-state"
            >
              <span class="empty-icon">🎁</span>
              <p class="empty-text">ยังไม่มีรายการคอมมิชชั่น</p>
            </div>

            <template v-else>
              <div
                v-for="(item, index) in history"
                :key="index"
                class="list-row"
              >
                <div class="row-avatar">
                  {{ item.type === 'first_deposit' ? '🎉' : '🏆' }}
                </div>
                <div class="row-main">
                  <div class="row-name">
                    {{
                      item.type === 'first_deposit'
                        ? 'ฝากแรกเพื่อน'
                        : 'ยอดเสียเพื่อน'
                    }}
                    · ชั้น {{ item.level }}
                  </div>
                  <div class="row-sub">
                    <template v-if="item.period_key"
                      >รอบ {{ item.period_key }} ·
                    </template>
                    จาก {{ item.source_phone }}
                  </div>
                </div>
                <span class="row-amount"
                  >+฿{{ formatNumber(item.amount) }}</span
                >
                <span class="pill" :class="`pill-${item.status}`">
                  {{ statusLabel(item.status) }}
                </span>
              </div>

              <button
                v-if="historyHasMore"
                class="load-more-btn"
                :disabled="historyLoading"
                @click="fetchHistory()"
              >
                {{ historyLoading ? 'กำลังโหลด...' : 'โหลดเพิ่ม' }}
              </button>
            </template>
          </div>
        </template>
      </div>

      <!-- Friend Activity Bottom Sheet -->
      <div
        v-if="showFriendSheet"
        class="sheet-overlay"
        @click.self="closeFriendSheet"
      >
        <div class="claim-sheet friend-sheet">
          <div class="sheet-grab" />

          <div v-if="selectedFriend" class="friend-head">
            <div class="row-avatar">🧑</div>
            <div class="row-main">
              <div class="row-name">{{ selectedFriend.phone_masked }}</div>
              <div class="row-sub">
                สมัคร {{ formatDate(selectedFriend.registered_at) }}
              </div>
            </div>
            <span
              class="tag"
              :class="selectedFriend.level === 1 ? 'tag-l1' : 'tag-l2'"
            >
              ชั้น {{ selectedFriend.level }}
            </span>
          </div>

          <div
            v-if="selectedFriend && selectedFriend.earned > 0"
            class="friend-earned"
          >
            รายได้สะสมจากเพื่อนคนนี้
            <strong>+฿{{ formatNumber(selectedFriend.earned) }}</strong>
          </div>

          <div v-if="friendDetailLoading" class="friend-loading">
            <b-spinner small variant="warning" />
          </div>

          <div v-else-if="friendDetailError" class="friend-loading">
            โหลดข้อมูลไม่สำเร็จ กรุณาลองอีกครั้ง
          </div>

          <template v-else-if="friendDetail">
            <div v-if="friendDetail.first_deposit" class="fd-line">
              <span class="fd-icon">🎉</span>
              <span class="fd-text">
                ฝากแรก ฿{{
                  formatNumber(friendDetail.first_deposit.base_amount)
                }}
                → คอมของคุณ
                <strong>
                  +฿{{ formatNumber(friendDetail.first_deposit.amount) }}
                </strong>
              </span>
              <span
                class="pill"
                :class="`pill-${friendDetail.first_deposit.status}`"
              >
                {{ statusLabel(friendDetail.first_deposit.status) }}
              </span>
            </div>

            <div class="period-list">
              <div
                v-for="period in friendDetail.periods"
                :key="period.period_key"
                class="period-item"
                :class="{ 'period-current': period.current }"
              >
                <div class="period-head">
                  <span class="period-label">
                    {{ formatPeriodRange(period.start, period.end) }}
                  </span>
                  <span v-if="period.current" class="tag tag-current">
                    รอบปัจจุบัน
                  </span>
                  <span class="period-com">
                    <span v-if="period.current" class="period-accruing">
                      กำลังสะสม…
                    </span>
                    <template v-else-if="period.commission">
                      <span class="row-amount">
                        +฿{{ formatNumber(period.commission.amount) }}
                      </span>
                      <span
                        class="pill"
                        :class="`pill-${period.commission.status}`"
                      >
                        {{ statusLabel(period.commission.status) }}
                      </span>
                    </template>
                    <span v-else class="period-none">ไม่มีคอม</span>
                  </span>
                </div>
                <div class="period-stats">
                  <div v-if="!period.current" class="stat-mini">
                    <span class="stat-mini-label">ยอดเสียจริง</span>
                    <span class="stat-mini-value emphasize">
                      {{
                        period.basis !== null
                          ? `฿${formatNumber(period.basis)}`
                          : '—'
                      }}
                    </span>
                  </div>
                  <div class="stat-mini">
                    <span class="stat-mini-label">ฝาก</span>
                    <span class="stat-mini-value">
                      ฿{{ formatNumber(period.deposits) }}
                    </span>
                  </div>
                  <div class="stat-mini">
                    <span class="stat-mini-label">ถอน</span>
                    <span class="stat-mini-value">
                      ฿{{ formatNumber(period.withdrawals) }}
                    </span>
                  </div>
                </div>
                <div v-if="period.promo_deposits > 0" class="promo-note">
                  ฝากที่รับโปร ฿{{ formatNumber(period.promo_deposits) }}
                </div>
              </div>
            </div>

            <div class="fine-print">
              ยอดเสียจริงจากค่ายเกม หักโบนัสที่ได้รับในรอบ • คำนวณเมื่อปิดรอบ
            </div>
          </template>

          <button
            class="action-btn ghost-btn block-btn"
            @click="closeFriendSheet"
          >
            ปิด
          </button>
        </div>
      </div>

      <!-- Claim Bottom Sheet -->
      <div
        v-if="showClaimSheet"
        class="sheet-overlay"
        @click.self="closeClaimSheet"
      >
        <div class="claim-sheet">
          <div class="sheet-grab" />
          <h3 class="sheet-title">ยืนยันรับคอมมิชชั่น</h3>
          <div class="sheet-amount">
            ฿{{ formatNumber(summary.claimable_total) }}
          </div>
          <div v-if="summary.claim_turnover_multiplier > 0" class="sheet-note">
            ⚠️ เครดิตต้องทำเทิร์น
            {{ formatNumber(summary.claim_turnover_multiplier) }} เท่า (฿{{
              formatNumber(
                summary.claimable_total * summary.claim_turnover_multiplier
              )
            }}) ก่อนถอน
          </div>
          <button
            class="action-btn primary-btn block-btn"
            :disabled="isClaiming"
            @click="claimCommission"
          >
            <b-spinner v-if="isClaiming" small class="mr-50" />
            {{ isClaiming ? 'กำลังดำเนินการ...' : 'ยืนยันรับเลย' }}
          </button>
          <button
            class="action-btn ghost-btn block-btn"
            :disabled="isClaiming"
            @click="closeClaimSheet"
          >
            ยกเลิก
          </button>
        </div>
      </div>

      <!-- Info Modal -->
      <div
        v-if="showInfoModal"
        class="modal-overlay"
        @click.self="showInfoModal = false"
      >
        <div class="info-modal">
          <div class="modal-header">
            <h3 class="modal-title">📋 เงื่อนไขระบบชวนเพื่อน</h3>
            <button class="modal-close" @click="showInfoModal = false">
              ×
            </button>
          </div>
          <div class="modal-body">
            <ul class="terms-list">
              <li>
                แชร์ลิงก์ของคุณ เพื่อนสมัครผ่านลิงก์ = เป็นเพื่อนชั้น 1 ของคุณ
              </li>
              <li v-if="summary && summary.enabled">
                รับ
                <strong
                  >{{
                    ratePercent(summary.rates.first_deposit_level1_rate)
                  }}%</strong
                >
                จากยอดฝากครั้งแรกของเพื่อน
              </li>
              <li v-if="summary && summary.enabled">
                รับ
                <strong
                  >{{ ratePercent(summary.rates.winloss_level1_rate) }}%</strong
                >
                จากยอดเสียจริงของเพื่อน สะสม{{
                  periodLabel(summary.periods.winloss)
                }}
              </li>
              <li v-if="summary && summary.enabled && summary.level2_enabled">
                เพื่อนของเพื่อน (ชั้น 2) ก็สร้างคอมมิชชั่นให้คุณด้วย
              </li>
              <li>
                ฝากแรกที่รับโปรโมชั่นไม่นับคอมฝากแรก
                และโบนัสที่เพื่อนได้รับจะถูกหักจากยอดเสียก่อนคำนวณ
              </li>
              <li
                v-if="
                  summary && summary.enabled && summary.first_deposit_min > 0
                "
              >
                ฝากแรกของเพื่อนต้องอย่างน้อย
                {{ formatNumber(summary.first_deposit_min) }} บาท
              </li>
              <li>คอมมิชชั่นรับได้เมื่อปิดรอบ และต้องถึงยอดขั้นต่ำ</li>
              <li>เครดิตที่รับต้องทำเทิร์นตามที่กำหนดก่อนถอน</li>
              <li>สมัครผ่านอุปกรณ์เดียวกับผู้ชวนจะไม่ได้รับคอมมิชชั่น</li>
            </ul>
          </div>
          <div class="modal-actions">
            <button
              class="action-btn primary-btn block-btn"
              @click="showInfoModal = false"
            >
              เข้าใจแล้ว
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const PAGE_SIZE = 20

export default {
  name: 'ReferralPage',
  components: { BSpinner },
  data() {
    return {
      isLoading: true,
      loadError: false,
      summary: null,

      activeTab: 'friends',

      friends: [],
      friendsPage: 0,
      friendsHasMore: false,
      friendsLoading: false,
      friendFilter: 'all',
      friendsLoaded: false,

      history: [],
      historyPage: 0,
      historyHasMore: false,
      historyLoading: false,
      historyFilter: 'all',
      historyLoaded: false,

      hasCopied: false,
      copyResetTimer: null,

      showClaimSheet: false,
      isClaiming: false,
      showInfoModal: false,

      showFriendSheet: false,
      selectedFriend: null,
      friendDetail: null,
      friendDetailLoading: false,
      friendDetailError: false
    }
  },
  computed: {
    belowMinimum() {
      if (!this.summary) return true
      return this.summary.claimable_total < this.summary.min_claim_amount
    },
    claimProgress() {
      if (!this.summary || !this.summary.min_claim_amount) return 0
      return Math.min(
        100,
        (this.summary.claimable_total / this.summary.min_claim_amount) * 100
      )
    },
    totalFriends() {
      if (!this.summary) return 0
      return (this.summary.level1_count || 0) + (this.summary.level2_count || 0)
    },
    friendChips() {
      return [
        { key: 'all', label: `ทั้งหมด ${this.totalFriends}` },
        { key: 'level1', label: `ชั้น 1 · ${this.summary.level1_count}` },
        { key: 'level2', label: `ชั้น 2 · ${this.summary.level2_count}` },
        { key: 'deposited', label: `ฝากแล้ว · ${this.summary.deposited_count}` }
      ]
    },
    historyChips() {
      return [
        { key: 'all', label: 'ทั้งหมด' },
        { key: 'pending', label: 'รอปิดรอบ' },
        { key: 'claimable', label: 'รับได้' },
        { key: 'claimed', label: 'รับแล้ว' }
      ]
    }
  },
  async beforeMount() {
    await this.initialize()
  },
  beforeDestroy() {
    if (this.copyResetTimer) clearTimeout(this.copyResetTimer)
    document.body.style.overflow = ''
  },
  methods: {
    goBack() {
      this.$router.push('/dashboard').catch(() => {})
    },

    async initialize() {
      this.isLoading = true
      this.loadError = false
      try {
        const resp = await this.$http.get('/member/invite/summary')
        if (resp.data && resp.data.success) {
          this.summary = resp.data.data
          if (this.summary.enabled) {
            await this.fetchFriends(true)
          }
        } else {
          this.loadError = true
        }
      } catch (error) {
        console.error('[Referral] Error fetching summary:', error)
        this.loadError = true
      } finally {
        this.isLoading = false
      }
    },

    async refreshSummary() {
      try {
        const resp = await this.$http.get('/member/invite/summary')
        if (resp.data && resp.data.success) {
          this.summary = resp.data.data
        }
      } catch (error) {
        console.error('[Referral] Error refreshing summary:', error)
      }
    },

    switchTab(tab) {
      this.activeTab = tab
      if (tab === 'history' && !this.historyLoaded) {
        this.fetchHistory(true)
      }
      if (tab === 'friends' && !this.friendsLoaded) {
        this.fetchFriends(true)
      }
    },

    setFriendFilter(key) {
      if (this.friendFilter === key) return
      this.friendFilter = key
      this.fetchFriends(true)
    },

    setHistoryFilter(key) {
      if (this.historyFilter === key) return
      this.historyFilter = key
      this.fetchHistory(true)
    },

    async fetchFriends(reset = false) {
      if (this.friendsLoading) return
      this.friendsLoading = true
      if (reset) {
        this.friends = []
        this.friendsPage = 0
      }
      try {
        const page = this.friendsPage + 1
        const params = { page, size: PAGE_SIZE }
        if (this.friendFilter === 'level1') params.level = 1
        if (this.friendFilter === 'level2') params.level = 2
        if (this.friendFilter === 'deposited') params.deposited = 1

        const resp = await this.$http.get('/member/invite/members', { params })
        if (resp.data && resp.data.success && resp.data.data) {
          const rows = resp.data.data.rows || []
          this.friends = reset ? rows : this.friends.concat(rows)
          this.friendsPage = page
          this.friendsHasMore = rows.length === PAGE_SIZE
          this.friendsLoaded = true
        }
      } catch (error) {
        console.error('[Referral] Error fetching friends:', error)
      } finally {
        this.friendsLoading = false
      }
    },

    async fetchHistory(reset = false) {
      if (this.historyLoading) return
      this.historyLoading = true
      if (reset) {
        this.history = []
        this.historyPage = 0
      }
      try {
        const page = this.historyPage + 1
        const params = { page, size: PAGE_SIZE }
        if (this.historyFilter !== 'all') params.status = this.historyFilter

        const resp = await this.$http.get('/member/invite/commissions', {
          params
        })
        if (resp.data && resp.data.success && resp.data.data) {
          const rows = resp.data.data.rows || []
          this.history = reset ? rows : this.history.concat(rows)
          this.historyPage = page
          this.historyHasMore = rows.length === PAGE_SIZE
          this.historyLoaded = true
        }
      } catch (error) {
        console.error('[Referral] Error fetching history:', error)
      } finally {
        this.historyLoading = false
      }
    },

    async copyLink() {
      if (!this.summary || !this.summary.invite_link) return
      try {
        await this.$copyText(this.summary.invite_link)
        this.hasCopied = true
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'คัดลอกลิงก์แล้ว',
            icon: 'CheckCircleIcon',
            variant: 'success'
          }
        })
        if (this.copyResetTimer) clearTimeout(this.copyResetTimer)
        this.copyResetTimer = setTimeout(() => {
          this.hasCopied = false
        }, 3000)
      } catch (error) {
        console.error('[Referral] Copy failed:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'คัดลอกไม่สำเร็จ กรุณาลองอีกครั้ง',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      }
    },

    shareLine() {
      if (!this.summary || !this.summary.invite_link) return
      const text = `มาเล่นด้วยกัน สมัครผ่านลิงก์ของฉันเลย ${this.summary.invite_link}`
      window.open(
        `https://line.me/R/msg/text/?${encodeURIComponent(text)}`,
        '_blank'
      )
    },

    async openFriendDetail(friend) {
      this.selectedFriend = friend
      this.friendDetail = null
      this.friendDetailError = false
      this.showFriendSheet = true
      document.body.style.overflow = 'hidden'
      this.friendDetailLoading = true
      try {
        const resp = await this.$http.get(
          `/member/invite/members/${friend.id}/activity`
        )
        if (resp.data && resp.data.success) {
          this.friendDetail = resp.data.data
        } else {
          this.friendDetailError = true
        }
      } catch (error) {
        console.error('[Referral] Error fetching friend activity:', error)
        this.friendDetailError = true
      } finally {
        this.friendDetailLoading = false
      }
    },

    closeFriendSheet() {
      this.showFriendSheet = false
      document.body.style.overflow = ''
    },

    formatPeriodRange(start, end) {
      try {
        const s = new Date(start)
        const e = new Date(end)
        e.setDate(e.getDate() - 1)
        const opts = { day: 'numeric', month: 'short' }
        return `${s.toLocaleDateString('th-TH', opts)} – ${e.toLocaleDateString(
          'th-TH',
          opts
        )}`
      } catch (error) {
        return `${start} – ${end}`
      }
    },

    openClaimSheet() {
      if (this.belowMinimum) return
      this.showClaimSheet = true
      document.body.style.overflow = 'hidden'
    },

    closeClaimSheet() {
      if (this.isClaiming) return
      this.showClaimSheet = false
      document.body.style.overflow = ''
    },

    async claimCommission() {
      if (this.isClaiming) return
      this.isClaiming = true
      try {
        const resp = await this.$http.post('/member/invite/claim')
        if (resp.data && resp.data.success) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `รับคอมมิชชั่นสำเร็จ ฿${this.formatNumber(
                resp.data.amount
              )}`,
              text: 'เครดิตเข้าบัญชีแล้ว',
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })
          this.$eventBus.$emit('refreshBalance')
          this.showClaimSheet = false
          document.body.style.overflow = ''
          await this.refreshSummary()
          if (this.historyLoaded) await this.fetchHistory(true)
        } else {
          throw new Error(resp.data && resp.data.message)
        }
      } catch (error) {
        console.error('[Referral] Claim failed:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title:
              error.response?.data?.message ||
              'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.isClaiming = false
      }
    },

    formatNumber(value) {
      return new Intl.NumberFormat('th-TH', {
        maximumFractionDigits: 2
      }).format(value || 0)
    },

    formatDate(value) {
      if (!value) return '-'
      try {
        return new Date(value).toLocaleDateString('th-TH', {
          day: 'numeric',
          month: 'short',
          year: '2-digit'
        })
      } catch (error) {
        return value
      }
    },

    ratePercent(rate) {
      return this.formatNumber((rate || 0) * 100)
    },

    periodLabel(period) {
      return period === 'monthly' ? 'ทุกเดือน' : 'ทุกสัปดาห์'
    },

    statusLabel(status) {
      const labels = {
        pending: 'รอปิดรอบ',
        claimable: 'รับได้',
        claimed: 'รับแล้ว'
      }
      return labels[status] || status
    }
  }
}
</script>

<style lang="scss" scoped>
// ==========================================
// Variables
// ==========================================
$gold: var(--theme-secondary);
$card-bg: rgba(255, 255, 255, 0.05);
$border-color: rgba(255, 255, 255, 0.1);
$text-primary: #ffffff;
$text-secondary: rgba(255, 255, 255, 0.7);
$text-muted: rgba(255, 255, 255, 0.5);

// ==========================================
// Page Container
// ==========================================
.referral-page {
  width: 100%;
  min-height: 100%;
}

.page-content {
  max-width: 440px;
  margin: 0 auto;
  padding: 16px 16px 100px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// ==========================================
// Header
// ==========================================
.page-header {
  display: flex;
  align-items: center;
  gap: 12px;

  .page-title {
    flex: 1;
    font-size: 1.25rem;
    font-weight: 700;
    color: $text-primary;
    margin: 0;
  }

  .back-btn,
  .info-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: $card-bg;
    color: $text-secondary;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
}

// ==========================================
// States
// ==========================================
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 0;

  .loading-text {
    color: $text-secondary;
    margin: 0;
  }
}

.state-card {
  background: $card-bg;
  border: 1px solid $border-color;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  text-align: center;
  padding: 40px 20px;

  .state-icon {
    font-size: 2.5rem;
    display: block;
    margin-bottom: 12px;
  }

  .state-text {
    color: $text-primary;
    font-weight: 600;
    margin: 0 0 4px;
  }

  .state-subtext {
    color: $text-muted;
    font-size: 0.9375rem;
    margin: 0;
  }

  .retry-btn {
    margin-top: 12px;
    border: 1px solid $border-color;
    background: transparent;
    color: $text-primary;
    border-radius: 10px;
    padding: 8px 20px;
    cursor: pointer;
  }
}

// ==========================================
// Cards
// ==========================================
.main-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.glass-card {
  background: $card-bg;
  border: 1px solid $border-color;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  padding: 14px 16px;
}

.card-label {
  font-size: 0.8125rem;
  color: $text-muted;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

// Invite link card
.code-row {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .invite-code {
    font-family: 'SF Mono', ui-monospace, Consolas, monospace;
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: $gold;
  }

  .code-emoji {
    font-size: 1.25rem;
  }
}

.invite-link {
  font-size: 0.8125rem;
  color: $text-muted;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-row {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

// Buttons
.action-btn {
  flex: 1;
  border: none;
  border-radius: 10px;
  padding: 10px 0;
  font-size: 0.9375rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.primary-btn {
  background: var(--theme-gradient-primary);
  color: #fff;
}

.line-btn {
  background: #06c755;
  color: #fff;
}

.ghost-btn {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: $text-secondary;
}

.block-btn {
  width: 100%;
  border-radius: 12px;
  padding: 12px 0;
  font-size: 1rem;
  margin-top: 10px;
}

// Hero card
.hero-card {
  text-align: center;
}

.hero-amount {
  font-size: 2.25rem;
  font-weight: 800;
  color: $gold;
  font-variant-numeric: tabular-nums;
}

.hero-hint {
  font-size: 0.8125rem;
  color: $text-muted;
  margin-top: 4px;
}

.progress-track {
  height: 6px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  margin: 10px 24px 0;
  overflow: hidden;

  .progress-fill {
    height: 100%;
    border-radius: 4px;
    background: var(--theme-gradient-primary);
    transition: width 0.3s ease;
  }
}

// Split stats
.split-row {
  display: flex;
  gap: 12px;

  .stat-card {
    flex: 1;
    padding: 10px 14px;
  }

  .stat-amount {
    font-size: 1.25rem;
    font-weight: 700;
    color: $text-primary;
    font-variant-numeric: tabular-nums;
  }
}

// Rates strip
.rates-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: $text-secondary;
  background: rgba(var(--theme-primary-rgb), 0.12);
  border: 1px solid rgba(var(--theme-primary-rgb), 0.3);
  border-radius: 12px;
  padding: 8px 12px;

  strong {
    color: $text-primary;
  }
}

// ==========================================
// Tabs + Chips
// ==========================================
.tab-row {
  display: flex;
  gap: 8px;
}

.tab-btn {
  flex: 1;
  border: 1px solid $border-color;
  background: $card-bg;
  color: $text-muted;
  border-radius: 10px;
  padding: 9px 0;
  font-size: 0.9375rem;
  font-weight: 700;
  cursor: pointer;

  &.active {
    color: $text-primary;
    background: rgba(var(--theme-primary-rgb), 0.25);
    border-color: rgba(var(--theme-primary-rgb), 0.6);
  }
}

.chip-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.chip {
  border: none;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 0.8125rem;
  background: rgba(255, 255, 255, 0.08);
  color: $text-secondary;
  cursor: pointer;

  &.active {
    background: rgba(var(--theme-primary-rgb), 0.3);
    color: $text-primary;
  }
}

// ==========================================
// Lists
// ==========================================
.list-card {
  padding: 4px 14px;
}

.list-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);

  &:last-child {
    border-bottom: none;
  }
}

.row-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex: none;
  background: rgba(var(--theme-primary-rgb), 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.row-main {
  flex: 1;
  min-width: 0;
}

.row-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: $text-primary;
}

.row-sub {
  font-size: 0.75rem;
  color: $text-muted;
}

.row-amount {
  font-size: 0.9375rem;
  font-weight: 700;
  color: $gold;
  font-variant-numeric: tabular-nums;
  flex: none;

  &.zero {
    color: $text-muted;
  }
}

.tag {
  flex: none;
  font-size: 0.6875rem;
  font-weight: 700;
  border-radius: 6px;
  padding: 2px 6px;
}

.tag-l1 {
  background: rgba(var(--theme-primary-rgb), 0.3);
  color: $text-primary;
}

.tag-l2 {
  background: rgba(255, 255, 255, 0.12);
  color: $text-secondary;
}

.tag-deposit {
  background: rgba(46, 204, 113, 0.2);
  color: #7be0a8;
}

.pill {
  flex: none;
  font-size: 0.6875rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 3px 8px;
}

.pill-pending {
  background: rgba(255, 193, 7, 0.15);
  color: #ffd76b;
}

.pill-claimable {
  background: rgba(46, 204, 113, 0.18);
  color: #7be0a8;
}

.pill-claimed {
  background: rgba(255, 255, 255, 0.1);
  color: $text-muted;
}

.load-more-btn {
  width: 100%;
  border: none;
  background: transparent;
  color: $text-muted;
  font-size: 0.875rem;
  padding: 10px 0;
  cursor: pointer;
}

.empty-state {
  text-align: center;
  padding: 28px 12px;

  .empty-icon {
    font-size: 2.25rem;
    display: block;
    margin-bottom: 8px;
  }

  .empty-text {
    color: $text-secondary;
    font-size: 0.9375rem;
    font-weight: 600;
    margin: 0 0 4px;
  }

  .empty-subtext {
    color: $text-muted;
    font-size: 0.8125rem;
    margin: 0 0 12px;
  }

  .action-btn {
    max-width: 200px;
  }
}

// ==========================================
// Claim Bottom Sheet
// ==========================================
.sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1050;
  display: flex;
  align-items: flex-end;
}

.claim-sheet {
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  background: var(--theme-card-bg, #1a1a2e);
  border-radius: 22px 22px 0 0;
  border-top: 1px solid rgba(var(--theme-primary-rgb), 0.4);
  padding: 14px 16px calc(20px + env(safe-area-inset-bottom));

  .sheet-grab {
    width: 36px;
    height: 4px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.25);
    margin: 0 auto 12px;
  }

  .sheet-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: $text-primary;
    text-align: center;
    margin: 0 0 10px;
  }

  .sheet-amount {
    text-align: center;
    font-size: 2.25rem;
    font-weight: 800;
    color: $gold;
    font-variant-numeric: tabular-nums;
  }

  .sheet-note {
    font-size: 0.8125rem;
    color: #ffd76b;
    background: rgba(255, 193, 7, 0.08);
    border: 1px solid rgba(255, 193, 7, 0.25);
    border-radius: 10px;
    padding: 8px 10px;
    margin: 12px 0 4px;
    text-align: center;
  }
}

// ==========================================
// Friend Activity Sheet
// ==========================================
.list-row.tappable {
  cursor: pointer;

  &:active {
    background: rgba(255, 255, 255, 0.04);
  }
}

.row-chevron {
  color: rgba(255, 255, 255, 0.3);
  flex: none;
}

.friend-sheet {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.friend-head {
  display: flex;
  align-items: center;
  gap: 9px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 10px;
}

.friend-loading {
  text-align: center;
  padding: 24px 0;
  color: $text-muted;
  font-size: 0.875rem;
}

.fd-line {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: $text-secondary;
  background: rgba(var(--theme-primary-rgb), 0.12);
  border: 1px solid rgba(var(--theme-primary-rgb), 0.3);
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 10px;

  .fd-text {
    flex: 1;

    strong {
      color: $gold;
    }
  }
}

.period-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.period-item {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 9px 12px;

  &.period-current {
    background: rgba(255, 193, 7, 0.05);
    border-color: rgba(255, 193, 7, 0.25);
  }
}

.friend-earned {
  font-size: 0.8125rem;
  color: $text-secondary;
  background: rgba(var(--theme-primary-rgb), 0.12);
  border: 1px solid rgba(var(--theme-primary-rgb), 0.3);
  border-radius: 10px;
  padding: 7px 10px;
  margin-bottom: 10px;
  text-align: center;

  strong {
    color: $gold;
    font-size: 0.9375rem;
  }
}

.period-accruing {
  font-size: 0.8125rem;
  color: #ffd76b;
}

.period-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;

  .period-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: $text-primary;
  }

  .period-com {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .period-none {
    color: $text-muted;
    font-size: 0.875rem;
  }
}

.tag-current {
  background: rgba(255, 193, 7, 0.15);
  color: #ffd76b;
}

.period-stats {
  display: flex;
  gap: 6px;
}

.stat-mini {
  flex: 1;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;

  .stat-mini-label {
    font-size: 0.6875rem;
    color: $text-muted;
  }

  .stat-mini-value {
    font-size: 0.875rem;
    font-weight: 700;
    color: $text-secondary;
    font-variant-numeric: tabular-nums;

    &.emphasize {
      color: $gold;
    }
  }
}

.promo-note {
  margin-top: 6px;
  font-size: 0.75rem;
  color: #ffd76b;
}

.fine-print {
  font-size: 0.75rem;
  color: $text-muted;
  text-align: center;
  margin-top: 10px;
}

// ==========================================
// Info Modal
// ==========================================
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.info-modal {
  width: 100%;
  max-width: 360px;
  background: var(--theme-card-bg, #1a1a2e);
  border: 1px solid $border-color;
  border-radius: 16px;
  overflow: hidden;

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid $border-color;
  }

  .modal-title {
    font-size: 1rem;
    font-weight: 700;
    color: $text-primary;
    margin: 0;
  }

  .modal-close {
    border: none;
    background: transparent;
    color: $text-muted;
    font-size: 1.25rem;
    cursor: pointer;
    line-height: 1;
  }

  .modal-body {
    padding: 14px 16px;
  }

  .terms-list {
    margin: 0;
    padding-left: 18px;
    color: $text-secondary;
    font-size: 0.875rem;

    li {
      margin-bottom: 6px;

      strong {
        color: $text-primary;
      }
    }
  }

  .modal-actions {
    padding: 0 16px 16px;
  }
}

// ==========================================
// Desktop
// ==========================================
@media (min-width: 768px) {
  .page-content {
    padding-top: 24px;
  }
}
</style>
